import { Form, Formik } from "formik";
import { object, string } from "yup";
import { useState } from "react";
import useProperties from "../../../hooks/use-properties";
import { ReactSelect } from "../../UI/Select";
import TextInput from "../../UI/TextInput";
import PropertyDisplaySmallCard from "../../Property/PropertyDisplaySmallCard";
import FormikSubmitButton from "../../UI/FormikSubmitButton";
import CopyToClipboard from "../../UI/CopyToClipboard";
import Button from "../../UI/Button";
import { ModalDialogFooter } from "../../UI/ModalDialog";
import useAuthenticatedAxios from "../../../hooks/use-authenticated-axios";
import Card from "../../UI/Card";
import { sanitizeValues } from "../../../models/utils";
import { CircleCheck, Email, User } from "../../UI/Icons";
import IconAndText from "../../UI/IconAndText";
import ButtonCard from "../../UI/ButtonCard";
import TenantAddForm from "./TenantAddForm";
import { handleAPIError } from "../../../utils/index.jsx";

export default function CandidatAddForm({
  modal: ModalElement,
  propertyId,
  onClose,
}) {
  const axios = useAuthenticatedAxios();
  const { properties } = useProperties();
  const propertyOptions = properties.map((p) => ({
    label: p.nom,
    value: p.id,
  }));
  const [isFormSent, setIsFormSent] = useState(false);
  const [mode, setMode] = useState(null);

  async function submitInviteHandler(values) {
    if (!values.mail) {
      return;
    }
    try {
      await axios.post("bailleur/candidat", sanitizeValues(values));
      setIsFormSent(true);
    } catch (error) {
      handleAPIError(error);
    }
  }

  return (
    <>
      {mode === "create" && (
        <TenantAddForm
          modal={ModalElement}
          propertyId={propertyId}
          isCandidatOnly
        />
      )}
      {mode !== "create" && (
        <ModalElement size={"intermediate"}>
          {!isFormSent && !mode && (
            <>
              <p className={"text-center cardTitle marginB20"}>
                Ajouter un candidat
              </p>
              <div className="flex gap10 marginB20">
                <ButtonCard
                  className="flex-grow padding10"
                  onClick={() => setMode("invite")}
                >
                  <IconAndText text={"Inviter le candidat"} icon={<Email />} />
                </ButtonCard>
                <ButtonCard
                  className="flex-grow padding10"
                  onClick={() => setMode("create")}
                >
                  <IconAndText text={"Créer manuellement"} icon={<User />} />
                </ButtonCard>
              </div>
              <Card padding={"md"} className="bg-grey">
                <p>
                  <strong>Inviter le candidat :</strong> il complète lui-même
                  son dossier.
                </p>
                <p className="marginT10">
                  <strong>Créer manuellement :</strong> saisissez vous-même les
                  informations du candidat.
                </p>
              </Card>
            </>
          )}
          {!isFormSent && mode === "invite" && (
            <>
              <div className={"cardTitle text-center"}>
                <img
                  src="/photo_invite_candidat.jpg"
                  alt="Invitation candidat"
                />
              </div>
              <p className={"text-center cardTitle marginB20"}>
                Inviter un candidat
              </p>
              <div className="marginB20">
                <IconAndText
                  icon={<CircleCheck />}
                  text={
                    <p className="marginB0">
                      Permet d'inviter un candidat à déposer son dossier pour
                      votre bien.
                    </p>
                  }
                  className="marginB5"
                />
                <IconAndText
                  icon={<CircleCheck />}
                  text={
                    <p className="marginB0">
                      Lorsque le candidat aura complété et envoyé son dossier,
                      vous serez informé.
                    </p>
                  }
                  className="marginB5"
                />
                <IconAndText
                  icon={<CircleCheck />}
                  text={
                    <p className="marginB0">
                      Vous pourrez l'étudier et convertir le candidat en
                      locataire (création de bail, etc...).
                    </p>
                  }
                />
              </div>
              <Formik
                initialValues={{
                  id_bien: propertyId ? propertyId.toString() : "",
                  mail: "",
                }}
                enableReinitialize={true}
                validationSchema={object({
                  mail: string()
                    .email("Adresse e-mail invalide")
                    .required("Veuillez spécifier une adresse e-mail"),
                  id_bien: string().required("Veuillez sélectionner un bien"),
                })}
                onSubmit={submitInviteHandler}
              >
                {({ values }) => {
                  const link = `${
                    import.meta.env.VITE_FRONTEND_URL
                  }/candidat?bien=${values.id_bien}`;
                  return (
                    <Form>
                      {!propertyId && (
                        <ReactSelect
                          name="id_bien"
                          label={"Bien concerné par la candidature"}
                          options={propertyOptions}
                          className={"marginB10"}
                          mandatory
                        />
                      )}
                      {values.id_bien && (
                        <div className="marginB20">
                          {properties
                            .filter(
                              (filtered) =>
                                filtered.id === parseInt(values.id_bien),
                            )
                            .map((property) => (
                              <PropertyDisplaySmallCard
                                property={property}
                                key={property.id}
                              />
                            ))}
                          <Card padding={"md"} className="bg-grey">
                            <Card className="marginB10" padding="md">
                              <p className="primaryText marginB5">
                                Envoyez une invitation par email directement
                                depuis Qalimo
                              </p>
                              <TextInput
                                type="text"
                                name="mail"
                                placeholder="Ex : jean.d@mail.com"
                                label={"Adresse e-mail du candidat *"}
                              />
                            </Card>
                            <Card className="marginB10" padding={"md"}>
                              <p className="primaryText marginB5">
                                Envoyez directement le lien par message,
                                Whatsapp, email ou à insérer sur une
                                petite-annonce (⚠️ certaines plateformes
                                bloquent les liens)
                              </p>
                              <span className="primaryText">
                                Lien de candidature :
                              </span>
                              <span> {link}</span>
                              <CopyToClipboard toCopy={link} />
                            </Card>
                            <Card padding={"md"}>
                              <p className="primaryText marginB5">
                                Copiez le message suivant sur les différents
                                sites d'annonce
                              </p>
                              <p
                                className="secondaryText marginT5"
                                padding={"md"}
                              >
                                Pour soumettre votre candidature, rendez vous
                                sur le site de Qalimo, espace candidature, et
                                entrez le numéro de bien :{" "}
                                <strong>{values.id_bien}</strong> Vous serez
                                invité à créer un compte, puis à compléter votre
                                dossier en ligne. Vos données sont sécurisées,
                                logiciel français et créé par un ancien huissier
                                de justice.
                              </p>
                            </Card>
                          </Card>
                        </div>
                      )}
                      <ModalDialogFooter
                        start={
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setMode(null)}
                          >
                            Retour
                          </Button>
                        }
                      >
                        <FormikSubmitButton>
                          Envoyer l'invitation
                        </FormikSubmitButton>
                      </ModalDialogFooter>
                    </Form>
                  );
                }}
              </Formik>
            </>
          )}
          {isFormSent && (
            <>
              <h2>Invitation envoyée avec succès</h2>
              <p>L'invitation a été envoyée à votre candidat.</p>
              <p className={"marginT10"}>
                Vous serez alerté lorsqu'il aura complété son dossier.
              </p>
              <p className={"marginT10"}>
                Vous pourrez ensuite étudier le dossier, et approuver ou non le
                candidat.
              </p>
              <div className={"text-right marginT20"}>
                <Button onClick={onClose}>Fermer</Button>
              </div>
            </>
          )}
        </ModalElement>
      )}
    </>
  );
}
